import { useSelector, shallowEqual } from 'react-redux';

import {
  selectTariffDetailsErrors,
  setCurrentTariff,
  selectIsEconomy7,
  setIsEconomy7,
  resetTariffDetails,
  initialState
} from 'state/tariff-information/tariff-information-slice';
import { getTariffDetailsThunk } from 'state/tariff-information/tariff-information-thunks';
import { useNavigation } from 'hooks/useNavigation';
import { useTariffInfo } from '../hooks/useTariffInfo';
import { NsSelect } from 'components/common/ns-select/NsSelect';
import { Error } from 'components/common/error/Error';
import { NsPrimaryCta } from 'components/common/ns-primary-cta/NsPrimaryCta';
import { NsForm } from 'components/common/ns-form/NsForm';
import { NsCheckboxInput } from 'components/common/ns-checkbox-input/NsCheckboxInput';
import { NsSecondaryCta } from 'components/common/ns-secondary-cta/NsSecondaryCta';
import { useAppDispatch } from 'hooks/useAppDispatch';

export const testId = 'tariffSelectionTestId';

type TariffSelectionProps = {
  paymentMethod: 'qcc' | 'mdd';
  fuel: string;
  postcode: string;
  isGasOnly?: boolean;
};

export const TariffSelection = ({
  isGasOnly = false,
  fuel,
  paymentMethod,
  postcode
}: TariffSelectionProps) => {
  const dispatch = useAppDispatch();
  const { eventTypes, next } = useNavigation();
  const { tariffs, currentTariff, isLoadingTariffDetails } = useTariffInfo();

  const isEconomy7 = useSelector(selectIsEconomy7, shallowEqual);

  const isLoading = isLoadingTariffDetails;
  const errors = useSelector(selectTariffDetailsErrors, shallowEqual) || [];
  const { isTOU, id: currentTariffId } = currentTariff || {};
  const showE7Checkbox = !isGasOnly && !isTOU;

  const handleChangeTariff = (event) => {
    const {
      target: { value }
    } = event;
    const tariff = tariffs.find(({ id }) => id === value);

    dispatch(setCurrentTariff(tariff));
    dispatch(resetTariffDetails());
    dispatch(setIsEconomy7(initialState.isEconomy7));
  };

  const handleChangeIsEconomy7 = () => {
    dispatch(setIsEconomy7(!isEconomy7));
  };

  const handleViewTarifffDetails = async () => {
    try {
      await dispatch(
        getTariffDetailsThunk({
          tariffId: currentTariffId,
          paymentMethod,
          economy7: isEconomy7,
          isTOU,
          fuel,
          postcode,
          requestConfig: {}
        })
      );
    } catch (error) {
      error && console.error(error);
    }
  };

  const handleCantFindTariff = () => {
    next({
      eventType: eventTypes.BG_TARIFF_INFORMATION,
      queryParams: {
        postcode,
        fuel,
        'payment-type': paymentMethod
      }
    });
  };

  return (
    <NsForm testId={testId} onSubmit={handleViewTarifffDetails}>
      <NsSelect
        name="selectAddress"
        value={currentTariffId}
        onChange={handleChangeTariff}
        id="tariffList"
        options={tariffs.map((tariff) => {
          return {
            value: tariff.id,
            text: tariff.displayName
          };
        })}
        label="Choose a tariff to see the details"
        placeholder="Please select a tariff"
      />

      {showE7Checkbox && (
        <NsCheckboxInput
          name="isEconomy7"
          onChange={handleChangeIsEconomy7}
          value={isEconomy7}
          key={isEconomy7}
          options={[
            {
              label: 'I have an Economy 7 meter for my electricity',
              value: 'true'
            }
          ]}
        />
      )}
      <Error errors={errors} />
      <NsPrimaryCta isLoading={isLoading}>View tariff information</NsPrimaryCta>
      {!currentTariff && (
        <NsSecondaryCta onClick={handleCantFindTariff}>
          Can&apos;t find your tariff? View more tariff information here
        </NsSecondaryCta>
      )}
    </NsForm>
  );
};
